import { useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useIntl } from 'react-intl'
import { Controller, useForm } from 'react-hook-form'
import { CaretLeft } from 'phosphor-react'
import { motion, AnimatePresence } from 'framer-motion'

import { Button } from './Button'
import { Radio } from './Radio'
import { useTranslation } from '../contexts/TranslationContext'
import { type Locale } from '../i18n/I18nProvider'

interface SkillsMobileMenuProps {
  isOpen: boolean
  onClose: () => void
}

interface FormValues {
  selectedLocale: Locale
}

export const SkillsMobileMenu = ({
  isOpen,
  onClose
}: SkillsMobileMenuProps) => {
  const [currentLocale, setCurrentLocale] = useTranslation()
  const { control, handleSubmit, formState } = useForm<FormValues>()
  const { formatMessage } = useIntl()

  const languages = useMemo(() => {
    return [
      {
        locale: 'en',
        name: formatMessage({ id: 'ENGLISH' })
      },
      {
        locale: 'pt',
        name: formatMessage({ id: 'PORTUGUESE' })
      },
      {
        locale: 'es',
        name: formatMessage({ id: 'SPANISH' })
      }
    ]
  }, [formatMessage])

  const onUpdateAppLanguage = ({ selectedLocale }: FormValues) => {
    setCurrentLocale(selectedLocale)
  }

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={
            'h-full flex flex-col px-8 py-8 fixed overflow-y-scroll z-20 top-0 right-0 bottom-0 overflow-hidden left-0 bg-white'
          }
        >
          <header className="mb-16">
            <button onClick={onClose} className="block mb-12">
              <CaretLeft className="text-2xl" />
            </button>
          </header>

          <form
            onSubmit={handleSubmit(onUpdateAppLanguage)}
            className="flex flex-col grow"
          >
            <h2 className="mb-10 text-base font-medium">
              {formatMessage({ id: 'LANGUAGES' })}
            </h2>
            <div className="grow">
              <Controller
                name="selectedLocale"
                control={control}
                render={({ field: { onChange } }) => (
                  <Radio.Root
                    name="selectedLocale"
                    defaultValue={currentLocale}
                    onChange={onChange}
                  >
                    {languages.map(({ locale, name }) => (
                      <div key={locale} className="[&:not(:last-child)]:mb-6">
                        <Radio.Item id={locale} value={locale}>
                          <Radio.Label htmlFor={locale}>{name}</Radio.Label>
                        </Radio.Item>
                      </div>
                    ))}
                  </Radio.Root>
                )}
              />
            </div>

            <Button type="submit" fullWidth isLoading={formState.isSubmitting}>
              {formatMessage({ id: 'SAVE' })}
            </Button>
          </form>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}
